import React from "react";

import { breakpoints } from "@literati/public-ui-shared-react";
import useBookShuffle from "components/bookShuffle/useBookShuffle";
import { interpolate } from "components/bookShuffle/utils";

/**
 * How faw the top-most card has to be dragged before bottom
 * card has finished animating up to the original card's place.
 */
const DISTANCE_PX_UNTIL_FULL_SCALE = 150;
const SCALE_AMOUNT_START = 0.89;
const SCALE_AMOUNT_END = 1.0;
const TRANSLATE_DISTANCE_Y_REM_END = 0;
const BOX_SHADOW_OFFSET_X = 0;
const BOX_SHADOW_OFFSET_Y_PX_START = 4;
const BOX_SHADOW_OFFSET_Y_PX_END = 10;
const BOX_SHADOW_BLUR_RADIUS_PX_START = 4;
const BOX_SHADOW_BLUR_RADIUS_PX_END = 15;
const BOX_SHADOW_OPACITY_END = 0.15;

const getTranslateYDistanceRemStart = () =>
  typeof window === "object"
    ? window.innerWidth < breakpoints.lg
      ? 2.125
      : 2.375
    : 2.125;

interface Styles {
  wrapperStyle: React.CSSProperties;
  cardStyle: React.CSSProperties;
}

export default function useCardStyles(bookIndex: number): Styles | null {
  const { currentIndex, isAnimating, distanceSwiped, books } = useBookShuffle();

  const wrapperStyle: React.CSSProperties = {};
  const cardStyle: React.CSSProperties = {};

  let topCardIndex = currentIndex;
  if (isAnimating) topCardIndex -= 1;

  // apply styles to the card based on their position in the stack
  const BOX_SHADOW_OPACITY_START = 0.1 / (books?.length + 1 ?? 1);

  // do not display cards that have already been swiped
  if (bookIndex < topCardIndex) {
    return null;
  }
  // style the top card
  else if (bookIndex === topCardIndex) {
    wrapperStyle.transform = `scale(1)`;
    cardStyle.boxShadow = `${BOX_SHADOW_OFFSET_X}px ${BOX_SHADOW_OFFSET_Y_PX_END}px ${BOX_SHADOW_BLUR_RADIUS_PX_END}px rgba(0, 0, 0, ${BOX_SHADOW_OPACITY_END})`;
  }
  // style the card below the top card
  else if (bookIndex === topCardIndex + 1) {
    const percentCompleteSwiped = Math.max(
      Math.min(distanceSwiped / DISTANCE_PX_UNTIL_FULL_SCALE, 1),
      // show the card as the top card if the previous card is still animating off
      isAnimating ? 1 : 0
    );
    const nextCardScale = interpolate(
      SCALE_AMOUNT_START,
      SCALE_AMOUNT_END,
      percentCompleteSwiped
    );
    const nextCardTranslateRem = interpolate(
      getTranslateYDistanceRemStart(),
      TRANSLATE_DISTANCE_Y_REM_END,
      percentCompleteSwiped
    );
    const nextCardBoxShadowOffsetY = interpolate(
      BOX_SHADOW_OFFSET_Y_PX_START,
      BOX_SHADOW_OFFSET_Y_PX_END,
      percentCompleteSwiped
    );
    const nextCardBoxShadowBlurRadius = interpolate(
      BOX_SHADOW_BLUR_RADIUS_PX_START,
      BOX_SHADOW_BLUR_RADIUS_PX_END,
      percentCompleteSwiped
    );
    const nextCardBoxShadowOpacity = interpolate(
      BOX_SHADOW_OPACITY_START,
      BOX_SHADOW_OPACITY_END,
      percentCompleteSwiped
    );
    wrapperStyle.transformOrigin = "bottom-center";
    wrapperStyle.transform = `translateY(${nextCardTranslateRem}rem) scale(${nextCardScale})`;
    cardStyle.boxShadow = `${BOX_SHADOW_OFFSET_X}px ${nextCardBoxShadowOffsetY}px ${nextCardBoxShadowBlurRadius}px rgba(0, 0, 0, ${nextCardBoxShadowOpacity})`;
  }
  // style the card below the next card
  else {
    wrapperStyle.transform = `translateY(${getTranslateYDistanceRemStart()}rem) scale(${SCALE_AMOUNT_START})`;
    cardStyle.boxShadow = `${BOX_SHADOW_OFFSET_X}px ${BOX_SHADOW_OFFSET_Y_PX_START}px ${BOX_SHADOW_BLUR_RADIUS_PX_START}px rgba(0, 0, 0, ${BOX_SHADOW_OPACITY_START})`;
  }
  return {
    wrapperStyle,
    cardStyle,
  };
}
